import { createApi } from '@reduxjs/toolkit/query/react';
import { Language } from '@shared/enum';
import { apiBaseQuery } from '@shared/functions';

export type ErrorObject = {
    [key: string]: string;
};

export interface TournamentsSessions {
    tournament_session_id: number;
    tournament_id: number;
    guest_name: string;
    guest_contact?: string;
    started?: string;
    ended?: string;
    avatar?: string;
    position: number;
    total_points: number;
    total_duration?: number;
    overspeeding?: number;
    hit_cones?: number;
    completed_on_time?: number;
}

export interface TournamentKpis {
    id: number;
    name: string;
    formatting: string;
    is_key: boolean;
}
export interface TournamentsSessionV2 {
    id: number;
    guest_name: string;
    position: number;
    tournament_id: number;
    total_score: number;
    kpis: string;
    ended?: string;
}

export interface Tournament {
    id: number;
    name: string;
    is_guest_allowed: boolean;
    started_at?: string;
    completed_at?: string;
    lesson_id: number;
}

export interface TournamentMeta {
    tournament_id: number;
    name: string;
    started_at?: string;
    completed_at?: string;
    session_count: number;
}

export interface TournamentMetaFormated extends TournamentMeta {
    status: string;
}

export interface TournamentRequest {
    language: Language;
    tournamentID: number;
}

export interface TournamentsRequest {
    language: Language;
}

export interface TournamentResponse {
    status: number;
    message: string;
    data: {
        tournament: Tournament;
        sessions: TournamentsSessions[];
        kpis: TournamentKpis[];
        kpi_values: TournamentsSessionV2[];
    };
    error: ErrorObject;
}

export interface TournamentsResponse {
    status: number;
    message: string;
    data: TournamentMeta[];
    error: ErrorObject;
}

export interface TournamentComputersRequest {
    language: Language;
    tournamentID: number;
}

export interface TournamentComputer {
    computer_id: number;
    computer_name: string;
}

export interface TournamentComputersResponse {
    status: number;
    message: string;
    data: TournamentComputer[];
    error: ErrorObject;
}

export interface SocketRequest {
    language: Language;
    computerID: number;
    playerName: string;
    tournamentID: number;
}

export interface SocketResponse {
    status: number;
    message: string;
    data: {
        token: string;
        tournament_session_id: number;
    };
    error: ErrorObject;
}

export const tournamentApi = createApi({
    reducerPath: 'tournamentApi',
    baseQuery: apiBaseQuery,
    tagTypes: ['Tournament', 'Tournaments', 'TournamentComputers'],
    endpoints: (builder) => ({
        getTournament: builder.query<TournamentResponse, TournamentRequest>({
            query: (data) => ({
                url: `v1/tournament/retrieve_tournament?language=${encodeURIComponent(data.language.toLowerCase())}&tournament_id=${encodeURIComponent(data.tournamentID.toString().toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['Tournament'],
        }),
        getTournaments: builder.query<TournamentsResponse, TournamentsRequest>({
            query: (data) => ({
                url: `v1/tournament/retrieve_tournaments?language=${encodeURIComponent(data.language.toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['Tournaments'],
        }),
        getTournamentComputers: builder.query<TournamentComputersResponse, TournamentComputersRequest>({
            query: (data) => ({
                url: `v1/tournament/retrieve_computers?language=${encodeURIComponent(data.language.toLowerCase())}&tournament_id=${encodeURIComponent(data.tournamentID.toString().toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['TournamentComputers'],
        }),
        createSocketToken: builder.mutation<SocketResponse, SocketRequest>({
            query: (data) => ({
                url: `v1/tournament/retrieve_socket_token`,
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    computer_id: data.computerID,
                    player_name: data.playerName,
                    tournament_id: data.tournamentID,
                },
            }),
        }),
    }),
});

export const {
    useGetTournamentQuery,
    useGetTournamentsQuery,
    useGetTournamentComputersQuery,
    useCreateSocketTokenMutation,
} = tournamentApi;
